import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getRandomGrain } from "../lib/api";

/**
 * A Pinch of Salt™ — 365 Grains of Salt™ interactive section.
 *
 * The whole section is composed as a SINGLE illustrated scene in the
 * digital-storybook style of the existing Salt Block hero. It is NOT a
 * grid of clip-art. All four elements share the same painterly lighting,
 * camera angle, and warm golden-hour atmosphere:
 *
 *   1. The base scene painting: a warm farmhouse room painted around
 *      the visitor's real (unmodified) farmhouse table. Table pixels
 *      are photographed; everything else is a painterly Nano Banana
 *      illustration matching the Salt Block hero style. Cast shadow
 *      and warm rim light unify the table into the room.
 *   2. The antique glass salt shaker painted as a storybook prop
 *      (matching lighting, brass lid, Salty Moo seal etched on glass)
 *      sits on the tabletop. It's an image overlay so we can tilt it.
 *   3. The handwritten place card painted as a storybook prop —
 *      "Go ahead... Give it a gentle shake." — rests against the shaker.
 *   4. The parchment note painted as a storybook prop — pressed cosmos
 *      corner, deckled edges — appears on the tabletop after the shake
 *      with the current Grain of Salt overlaid on it.
 */

const ASSET = (name) => `/assets/365-grains/${name}`;
const HERO_SRC = ASSET("hero-porch.webp");
const SHAKER_SRC = ASSET("shaker.webp");
const PARCHMENT_SRC = ASSET("parchment.webp");

// Lid hole origins on the shaker.webp cutout, as percentages of the
// cutout's dimensions (848 × 1264). Used so falling salt particles
// emerge from the actual perforations painted on the brass lid,
// not from the middle or bottom of the shaker.
const LID_HOLES = [
  { x: 0.42, y: 0.16 },
  { x: 0.50, y: 0.14 },
  { x: 0.58, y: 0.16 },
  { x: 0.45, y: 0.20 },
  { x: 0.55, y: 0.20 },
];

// The standalone interaction section positions the shaker + parchment
// itself; no scene coordinates are needed.

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export default function PinchOfSalt() {
  const [tilt, setTilt] = useState(0);
  const [particles, setParticles] = useState([]);
  const [grain, setGrain] = useState(null);
  const [showParchment, setShowParchment] = useState(false);
  const [busy, setBusy] = useState(false);
  const idRef = useRef(0);
  const [reduce] = useState(
    typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  const spawnParticles = (tiltAtRelease) => {
    const now = ++idRef.current;
    const batch = [];
    LID_HOLES.forEach((hole, i) => {
      for (let k = 0; k < 3; k++) {
        batch.push({
          key: `${now}-${i}-${k}`,
          // origin as percent of the shaker cutout (which is the wrapper's
          // reference space); jitter for natural non-uniform flow.
          ox: hole.x + (Math.random() - 0.5) * 0.02,
          oy: hole.y + (Math.random() - 0.5) * 0.015,
          delay: 0.05 + Math.random() * 0.35,
          drift: (Math.random() - 0.5) * 12 + (tiltAtRelease > 0 ? 6 : -6),
          fall: 45 + Math.random() * 25, // % of shaker height
          tiltAtRelease,
        });
      }
    });
    setParticles(batch);
    setTimeout(() => {
      setParticles((prev) => (prev[0]?.key.startsWith(`${now}-`) ? [] : prev));
    }, 1800);
  };

  const handleShake = async () => {
    if (busy) return;
    setBusy(true);
    if (showParchment) setShowParchment(false);

    if (reduce) {
      try {
        const g = await getRandomGrain();
        setGrain(g);
        setShowParchment(true);
      } finally {
        setBusy(false);
      }
      return;
    }

    // Gentle rock — subtle counter-rotate first, then tip to the right,
    // release salt at peak tilt, then return upright.
    setTilt(-4);
    await wait(120);
    setTilt(22);
    await wait(90);
    spawnParticles(22);
    await wait(560);

    const grainPromise = getRandomGrain().catch(() => null);

    setTilt(8);
    await wait(180);
    setTilt(0);
    await wait(220);

    const g = await grainPromise;
    if (g) {
      setGrain(g);
      setShowParchment(true);
    }
    setBusy(false);
  };

  return (
    <section
      data-testid="pinch-of-salt"
      className="relative w-full pt-16 pb-20 sm:pt-20 sm:pb-28"
    >
      {/* Section title + subtitle sit above the illustrated scene so the
          heading feels like page copy on a storybook spread. */}
      <div className="max-w-3xl mx-auto text-center px-6">
        <h2
          className="font-script text-[#4B3A2F] leading-tight"
          style={{ fontSize: "clamp(2.4rem, 5vw, 3.6rem)" }}
          data-testid="pinch-heading"
        >
          365 Grains of Salt<sup className="text-[0.5em] align-super">™</sup>
        </h2>
        <div className="gold-rule mx-auto my-5" style={{ width: "6rem" }} />
        <p
          className="font-display italic text-[#4B3A2F]/85 mx-auto"
          style={{ fontSize: "clamp(1.05rem, 1.6vw, 1.35rem)", maxWidth: 560 }}
          data-testid="pinch-subtitle"
        >
          One grain. One Scripture. One quiet moment with God.
        </p>
      </div>

      {/* Illustrated porch scene — pure painterly artwork. Contains no
          shaker and no interactive elements. Simply tells the story of
          Mike and Stefanie starting the day with coffee, Bible, and
          quiet pasture. */}
      <div
        className="relative mx-auto mt-8 sm:mt-12"
        style={{ maxWidth: 1180, width: "100%" }}
      >
        <div
          className="relative w-full"
          style={{
            aspectRatio: "1195 / 896",
            maskImage:
              "radial-gradient(ellipse at 50% 55%, #000 65%, rgba(0,0,0,0.9) 88%, rgba(0,0,0,0) 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at 50% 55%, #000 65%, rgba(0,0,0,0.9) 88%, rgba(0,0,0,0) 100%)",
          }}
          data-testid="pinch-illustration"
        >
          <img
            src={HERO_SRC}
            alt="A peaceful Texas back porch at sunrise: Mike and Stefanie in rocking chairs — Stefanie reading her Bible, Mike holding his coffee — looking out across the pasture toward the Salty Moo barn. A small coffee cup and carafe rest on the table between them."
            className="absolute inset-0 w-full h-full object-cover select-none"
            draggable="false"
          />
        </div>
      </div>

      {/* Dedicated interaction section — sits beneath the illustration
          so the porch artwork has room to breathe while the shaker gets
          its own quiet stage. Heading, invitation, then the interactive
          shaker with the rising parchment above it. */}
      <div className="max-w-3xl mx-auto text-center px-6 pt-16 sm:pt-20">
        <h3
          className="font-script text-[#4B3A2F] leading-tight"
          style={{ fontSize: "clamp(2.0rem, 3.8vw, 2.9rem)" }}
          data-testid="pinch-interaction-heading"
        >
          Whenever you&rsquo;re ready&hellip;
        </h3>
        <p
          className="font-body text-[#4B3A2F]/85 mt-3 mx-auto"
          style={{
            fontSize: "clamp(1rem, 1.4vw, 1.15rem)",
            maxWidth: "34rem",
            lineHeight: 1.6,
          }}
          data-testid="pinch-interaction-subtitle"
        >
          Give the salt shaker a gentle shake to receive your{" "}
          <span className="italic">
            A Pinch of Salt<sup className="align-super">&trade;</sup>
          </span>
          .
        </p>

        {/* Interaction stage — the shaker sits centered here with room
            above for the parchment to rise from behind it. */}
        <div
          className="relative mx-auto mt-8"
          style={{
            width: "min(360px, 78vw)",
            height: "clamp(360px, 46vw, 500px)",
          }}
          data-testid="pinch-stage"
        >
          <StandaloneShaker
            onShake={handleShake}
            busy={busy}
            tilt={tilt}
            particles={particles}
            interacted={showParchment}
          />

          <AnimatePresence>
            {showParchment && grain && (
              <motion.div
                key={grain.id || grain.number}
                className="absolute left-1/2 select-none"
                data-testid="pinch-parchment"
                initial={{
                  opacity: 0,
                  scale: 0.4,
                  y: 60,
                  x: "-50%",
                  rotate: -6,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                  x: "-50%",
                  rotate: -2,
                }}
                exit={{ opacity: 0, scale: 0.9, y: 20, x: "-50%" }}
                transition={{
                  duration: 1.1,
                  ease: [0.22, 1, 0.36, 1],
                  opacity: { duration: 0.6, delay: 0.05 },
                }}
                style={{
                  top: "-12%",
                  width: "min(440px, 96%)",
                  transformOrigin: "50% 95%",
                  filter: "drop-shadow(0 14px 14px rgba(30,20,10,0.35))",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                <div
                  className="relative w-full"
                  style={{
                    // Container query context: text below sizes with the
                    // parchment's rendered inline width so nothing runs
                    // off the edges regardless of viewport.
                    containerType: "inline-size",
                    containerName: "parchment",
                  }}
                >
                  <img
                    src={PARCHMENT_SRC}
                    alt=""
                    aria-hidden="true"
                    className="w-full h-auto block select-none"
                    draggable="false"
                  />
                  {/* Text overlaid on the painted parchment. Consistent
                      margins on all four sides; contents scale with the
                      parchment so nothing runs off the edges. */}
                  <div
                    className="absolute flex items-center justify-center"
                    style={{
                      left: "13%",
                      right: "13%",
                      top: "13%",
                      bottom: "12%",
                    }}
                  >
                    <ParchmentText grain={grain} />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

/**
 * StandaloneShaker — the interactive salt shaker sized and positioned
 * for the dedicated interaction section BELOW the porch illustration
 * (as opposed to being overlaid on the tabletop inside the painting).
 * Centered horizontally in the stage; anchored to the bottom so the
 * parchment has room to rise from behind it.
 */
function StandaloneShaker({ onShake, busy, tilt, particles, interacted }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="absolute"
      style={{
        left: "50%",
        bottom: "0%",
        width: "min(150px, 42%)",
        aspectRatio: "848 / 1264",
        transform: "translateX(-50%)",
        transformOrigin: "50% 100%",
        zIndex: 3,
      }}
    >
      <motion.button
        type="button"
        onClick={onShake}
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        aria-label="Give the salt shaker a gentle shake to receive a Grain of Salt"
        data-testid="pinch-shaker"
        className="absolute inset-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A87149] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-md"
        disabled={busy}
        animate={
          !interacted && !busy && !hovered
            ? { scale: [1, 1.035, 1], rotate: 0 }
            : { scale: hovered && !busy ? 1.045 : 1, rotate: tilt }
        }
        transition={
          !interacted && !busy && !hovered
            ? { duration: 3.4, repeat: Infinity, ease: "easeInOut" }
            : {
                duration: 0.35,
                ease: "easeOut",
                rotate: { duration: 0.26, ease: [0.4, 0.1, 0.6, 1] },
              }
        }
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: busy ? "wait" : "pointer",
          transformOrigin: "50% 100%",
        }}
      >
        {/* Warm rim glow layer — fades in on hover / during idle pulse
            peaks so the shaker feels lit from within. */}
        <motion.span
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          animate={{
            opacity:
              hovered && !busy
                ? 0.85
                : !interacted && !busy
                ? [0.15, 0.35, 0.15]
                : 0,
          }}
          transition={
            !interacted && !busy && !hovered
              ? { duration: 3.4, repeat: Infinity, ease: "easeInOut" }
              : { duration: 0.35, ease: "easeOut" }
          }
          style={{
            background:
              "radial-gradient(ellipse at 50% 55%, rgba(255,220,150,0.75) 0%, rgba(255,220,150,0.35) 30%, rgba(255,220,150,0) 65%)",
            filter: "blur(6px)",
            borderRadius: "50%",
          }}
        />
        <img
          src={SHAKER_SRC}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-contain select-none pointer-events-none relative"
          draggable="false"
          style={{
            filter:
              hovered && !busy
                ? "drop-shadow(0 14px 12px rgba(30,20,10,0.4)) drop-shadow(0 0 10px rgba(255,220,150,0.55))"
                : "drop-shadow(0 10px 8px rgba(30,20,10,0.35))",
            transition: "filter 350ms ease-out",
          }}
        />
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ overflow: "visible" }}
          aria-hidden="true"
        >
          {particles.map((p) => (
            <SaltGrain key={p.key} p={p} />
          ))}
        </svg>
      </motion.button>
    </div>
  );
}

/**
 * SaltGrain — a single salt crystal falling from the shaker's lid.
 */
function SaltGrain({ p }) {
  // start position (as % of shaker box)
  const startX = p.ox * 100;
  const startY = p.oy * 100;
  // release direction: follow the shaker's tilt for the first stretch,
  // then gravity dominates the trajectory
  return (
    <motion.circle
      cx={startX}
      cy={startY}
      r="0.7"
      fill="#FBF3D6"
      initial={{ opacity: 0, x: 0, y: 0 }}
      animate={{
        opacity: [0, 1, 1, 0],
        x: [0, p.drift * 0.4, p.drift],
        y: [0, p.fall * 0.35, p.fall],
      }}
      transition={{
        delay: p.delay,
        duration: 1.2,
        ease: [0.35, 0, 0.9, 1],
        times: [0, 0.2, 0.8, 1],
      }}
      style={{
        filter: "drop-shadow(0 0 0.6px rgba(255,240,200,0.6))",
      }}
    />
  );
}

function ParchmentText({ grain }) {
  const size = grain?.collection_size ?? 365;
  const denom = size >= 365 ? "365" : `${size}`;
  // Font sizes are expressed in `cqi` (1cqi = 1% of the parchment's
  // inline width, thanks to the container-type on the parent) so every
  // line scales proportionally with the parchment itself. No clamping
  // on the viewport — the parchment governs its own typography.
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center text-center mx-auto"
      style={{ maxWidth: "100%" }}
    >
      <p
        className="uppercase text-[#7E9268]"
        style={{
          letterSpacing: "0.32em",
          fontSize: "2.6cqi",
          lineHeight: 1.2,
        }}
      >
        A Pinch of Salt<sup className="align-super">™</sup>
      </p>
      <p
        className="font-script text-[#4B3A2F]"
        style={{
          fontSize: "6.2cqi",
          lineHeight: 1.1,
          marginTop: "1cqi",
        }}
      >
        Grain #{grain.number} of {denom}
      </p>
      <p
        className="uppercase text-[#4B3A2F]"
        style={{
          letterSpacing: "0.22em",
          fontSize: "2.4cqi",
          lineHeight: 1.2,
          marginTop: "2cqi",
        }}
      >
        {grain.reference}{" "}
        <span className="text-[#7E9268] normal-case">(WEB)</span>
      </p>
      <blockquote
        className="font-display italic text-[#2C2724]"
        style={{
          lineHeight: 1.4,
          fontSize: "3.5cqi",
          marginTop: "2cqi",
        }}
      >
        &ldquo;{grain.scripture}&rdquo;
      </blockquote>
      <div
        aria-hidden
        style={{
          width: "12cqi",
          height: 1,
          marginTop: "2.4cqi",
          marginBottom: "2cqi",
          background:
            "linear-gradient(90deg, transparent, rgba(75,58,47,0.5), transparent)",
        }}
      />
      <p
        className="font-body text-[#2C2724]/90"
        style={{
          fontSize: "3.05cqi",
          lineHeight: 1.45,
        }}
      >
        {grain.reflection}
      </p>
    </div>
  );
}
